import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FaLeaf, 
  FaShieldAlt, 
  FaChartLine, 
  FaUsers, 
  FaGlobe, 
  FaLightbulb,
  FaHandshake,
  FaTrophy,
  FaRocket,
  FaLock,
  FaEye
} from 'react-icons/fa';

const ServiceCard = ({ icon, title, description, delay }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay }}
      viewport={{ once: true }}
      className="bg-white rounded-xl p-6 shadow-custom hover:shadow-custom-lg transition-all duration-300 transform hover:-translate-y-2"
    >
      <div className="bg-secondary/10 p-4 rounded-full w-16 h-16 flex items-center justify-center mb-6">
        {icon}
      </div>
      <h3 className="text-xl font-heading font-semibold mb-4 text-primary">{title}</h3>
      <p className="text-gray-600 leading-relaxed">{description}</p>
    </motion.div>
  );
};

const FeatureBlock = ({ title, description, isHighlighted = false }) => {
  return (
    <motion.div
      initial={{ opacity: 0, x: -20 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5 }}
      viewport={{ once: true }}
      className={`${isHighlighted ? 'bg-primary text-white' : 'bg-white'} rounded-lg p-6 shadow-sm border-l-4 border-secondary`}
    >
      <h4 className={`text-lg font-semibold mb-3 ${isHighlighted ? 'text-white' : 'text-primary'}`}>
        {title}
      </h4>
      <p className={`${isHighlighted ? 'text-white/90' : 'text-gray-600'}`}>
        {description}
      </p>
    </motion.div>
  );
};

const ComprehensiveServicesSection = () => {
  const sustainabilityServices = [
    {
      icon: <FaLeaf className="w-8 h-8 text-secondary" />,
      title: "Join us for a greener future",
      description: "As a more sustainable wealth manager, we have boldly integrated Net Zero and tackled climate change. Our company recognizes the urgency of the global climate crisis and has committed to reducing its carbon footprint. We're working towards a one-third reduction target in our carbon emissions by 2030 and committing to offset our emissions annually."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "Corporate Governance",
      description: "We believe that good corporate governance provides the foundation for high-performing organizations, and by embedding good practices, we'll preserve and strengthen stakeholders confidence and long term value."
    },
    {
      icon: <FaRocket className="w-8 h-8 text-secondary" />,
      title: "Future-proof your portfolio",
      description: "Investors are searching for opportunities to future-proof their portfolios by investing in the digital economy and the companies, ideas, and technologies that underpin it."
    }
  ];

  const approachFeatures = [
    {
      title: "Focus on Investment Opportunities and Performance",
      description: "By combining the depth of our expertise with the breadth of our platform, we aim to identify market opportunities and provide innovative solutions to help our clients reach their unique investment objectives. Our substantial scale and stability has made us a leader in this space for over half a decade."
    },
    {
      title: "Knowledge Transfer Across Leading Institutions",
      description: "We help sovereign institutions navigate shifting dynamics that impact both their existing asset allocations and long-term investment strategies."
    },
    {
      title: "Access to Global Thought Leaders",
      description: "Our global network and longstanding relationships allow us to identify relevant market trends and provide thoughtful, timely and innovative solutions."
    }
  ];

  const clientServices = [
    {
      icon: <FaUsers className="w-8 h-8 text-secondary" />,
      title: "The Craft of Relationship Banking",
      description: "Our relationship promise to all our customers is that you will deal directly with the decision maker. We won't just look at your credit scores, assets or security. Our experienced relationship bankers have the expertise to see and understand your business's opportunities and potential."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-secondary" />,
      title: "Grow Your Assets with Retail Investors",
      description: "We are on the cusp of a paradigm shift in terms of retail's role in a historically institutional dominated investment landscape. With Segway Capital consulting, you can access the retail market effortlessly. Retail capital is sticky, distributed, and growing fast."
    },
    {
      icon: <FaTrophy className="w-8 h-8 text-secondary" />,
      title: "Loyal Clients",
      description: "Our client base remains invested with high retention due to our network of unique investment products, ongoing educational tools, and technology infrastructure. Our separately-managed account (SMA) platform is not reliant on any single investor."
    }
  ];

  const communityValues = [
    { title: "Customer First", description: "We always put our customers first in everything." },
    { title: "Win Together", description: "Collaborative success through partnership and shared goals." },
    { title: "Innovation", description: "Continuously pushing boundaries with cutting-edge solutions." }
  ];

  const securityServices = [
    {
      icon: <FaShieldAlt className="w-8 h-8 text-secondary" />,
      title: "$450 Million Insurance",
      description: "Segway Capital partners with the foremost providers of security in the blockchain space, including Rahway insurance, ledger vault, among others. Collectively, they carry $450M in insurance protection for digital assets held in the platform via the most prestigious syndicate of underwriters."
    },
    {
      icon: <FaLock className="w-8 h-8 text-secondary" />,
      title: "Serious Security",
      description: "Secure customer and investor fund from cyber attacks, internal collision, and Human error with multi-layer technology that combines the latest breakthroughs in the MPC cryptography."
    },
    {
      icon: <FaEye className="w-8 h-8 text-secondary" />,
      title: "Guideline",
      description: "Segway Capital consulting believes that working with skilled security researchers across the globe is crucial in identifying weakness in the technology. If you have discovered a security vulnerability in our systems or in one of our products/services, we appreciate your help on disclosing it to us in a responsible manner."
    }
  ];

  return (
    <section className="py-20 bg-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 right-0 w-1/4 h-1/4 bg-secondary/5 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-0 w-1/4 h-1/4 bg-accent/5 rounded-full blur-3xl"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Sustainability Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-heading font-bold text-primary mb-6">Sustainable Financial Excellence</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Leading the future of responsible wealth management through innovative, sustainable, and forward-thinking investment strategies.
          </p>
          
          <div className="mb-12">
            <img 
              src="/assets/beautiful-view-wind-turbines-grass-covered-field-captured-holland_181624-6013.jpg_er2ofu.jpg" 
              alt="Sustainable energy and green future" 
              className="w-full max-w-4xl mx-auto h-64 md:h-72 object-cover rounded-2xl shadow-custom"
            />
          </div>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {sustainabilityServices.map((service, index) => (
            <ServiceCard 
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              delay={0.2 * index}
            />
          ))}
        </div>

        {/* Your Performance Our Priority Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-primary text-white rounded-2xl overflow-hidden mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-8 md:p-12">
              <h2 className="text-4xl font-heading font-bold mb-6">Your Performance, Our Priority.</h2>
              <p className="text-xl text-white/90 leading-relaxed">
                To turn today's reality into tomorrow's returns, you need a partner who puts you first- with an 
                immersive understanding of you, your goals, your potential. When you invest with us, your 
                success is our passion. Our knowledge, your knowledge. An enduring partnership, upheld 
                by deep responsibility. We continually bring you insights no one else can and convert them 
                into compelling opportunities- that we tailor to your needs. And then we do it again and 
                again, cultivating a long-term partnership based on conviction, sustainable outcomes, and 
                shared success over time.
              </p>
            </div>
            <div className="h-64 lg:h-auto">
              <img 
                src="/assets/business-team-working-together.jpg" 
                alt="Professional business team collaborating" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </motion.div>

        {/* Our Approach Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-20"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold text-primary mb-6">Our Approach</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              We help sovereign institutions navigate shifting dynamics that impact both their existing 
              asset allocations and long-term investment strategies. Our global network and longstanding 
              relationships allow us to identify relevant market trends and provide thoughtful, timely and innovative solutions.
            </p>
            
            <div className="mb-12">
              <img 
                src="/assets/co-workers-discussing-business-project.jpg" 
                alt="Professional business approach and consultation" 
                className="w-full max-w-5xl mx-auto h-64 md:h-80 object-cover rounded-2xl shadow-custom"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {approachFeatures.map((feature, index) => (
              <FeatureBlock 
                key={index}
                title={feature.title}
                description={feature.description}
              />
            ))}
          </div>
        </motion.div>

        {/* Client Services Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {clientServices.map((service, index) => (
            <ServiceCard 
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              delay={0.2 * index}
            />
          ))}
        </div>

        {/* Growth Potential Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-custom overflow-hidden mb-20"
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
            <div className="p-8 md:p-12">
              <h2 className="text-3xl font-heading font-bold text-primary mb-6">Growth Potential</h2>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Imagine seeing your firm's AUM base grow week over week as opposed to being limited to 
                finite fundraising windows. Continuous fundraising investment deposit can unlock your 
                AUM growth potential. We do all the heavy lifting so you can focus on investment 
                management and withdraw your profits.
              </p>
              <p className="text-gray-600 leading-relaxed">
                We have partnered with over 130 advisers in the United States, who are all registered 
                investment advisers, by FINRA/SEC and are registered broker-dealers. Giving us the 
                flexibility to partner in a variety of forms.
              </p>
            </div>
            <div className="p-8 md:p-12 bg-background">
              <h3 className="text-2xl font-heading font-bold text-primary mb-6">Own Your Future</h3>
              <p className="text-gray-600 leading-relaxed mb-6">
                <span className="font-semibold">Segway Capital</span> analysts have carefully studied the financial outline and demands of 
                modern asset management. We've developed a comprehensive system that combines all the 
                progress and best achievements of global financial experts.
              </p>
              <img 
                src="/assets/person-office-analyzing-checking-finance-graphs.jpg" 
                alt="Financial analysis and growth planning" 
                className="w-full h-48 object-cover rounded-lg shadow-lg"
              />
            </div>
          </div>
        </motion.div>

        {/* Community Values Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-4xl font-heading font-bold text-primary mb-6">Here We Soar</h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              The <span className="font-semibold">Segway Capital</span> community is a diverse and inclusive group of doers and dreamers. They are 
              banking, borrowing, and investing like never before. They are building and rebuilding their 
              credit. And getting more from each dollar with rewards and budgeting. Together, they are 
              taking control of their money, and they are finally able to dream big — even if they start 
              small. Here everyone gets a GPS to their dreams.
            </p>
            
            <div className="mb-12">
              <img 
                src="/assets/people-discussing-business-plan.jpg" 
                alt="Diverse team collaborating and planning" 
                className="w-full max-w-4xl mx-auto h-64 md:h-80 object-cover rounded-2xl shadow-custom"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {communityValues.map((value, index) => (
              <FeatureBlock 
                key={index}
                title={value.title}
                description={value.description}
                isHighlighted={index === 1}
              />
            ))}
          </div>
        </motion.div>

        {/* Security & Insurance Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl font-heading font-bold text-primary mb-6">Security & Insurance Excellence</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Protecting your investments with industry-leading security measures and comprehensive insurance coverage for complete peace of mind.
          </p>
          
          <div className="mb-12">
            <img 
              src="/assets/workmates-with-statistic-documents.jpg" 
              alt="Professional security analysis and documentation" 
              className="w-full max-w-4xl mx-auto h-64 md:h-72 object-cover rounded-2xl shadow-custom"
            />
          </div>
        </motion.div>

        {/* Security Services Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-20">
          {securityServices.map((service, index) => (
            <ServiceCard 
              key={index}
              icon={service.icon}
              title={service.title}
              description={service.description}
              delay={0.2 * index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ComprehensiveServicesSection; 